import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { Grid } from '@mui/material';
import useFeatureFlags from 'api/useFeatureFlags';
import useIntegration from 'api/useIntegration';

import AcCard from 'components/AcCard/AcCard';
import DataTable from 'components/DataTable/DataTable';
import FirstActionModal from 'components/FirstActionModal/FirstActionModal';
import CustomizedSwitch from 'components/SwitchButton/SwitchButton';

import { EEventCategories, EFeatureFlag } from '../../../constants/enums';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { EPublisherEventValue, PublisherEventType } from '../Settings.types';

interface EventCategorySectionProps {
  category: string;
  events: PublisherEventType[];
  enabledEvents: EPublisherEventValue[];
  enabledEventsV2: EPublisherEventValue[];
  isTableDataEmpty: boolean;
}

const EventCategorySection: React.FC<EventCategorySectionProps> = ({
  category,
  events,
  enabledEvents,
  enabledEventsV2,
  isTableDataEmpty
}) => {
  const { publisherId } = useParams();

  const { updateIntegration }: any = useIntegration(publisherId);
  const { featureFlags } = useFeatureFlags({ publisherId });

  const areDeeplinksEnabled = useMemo(
    () =>
      featureFlags?.[EFeatureFlag.DASHBOARD_DEEPLINKS],
    [featureFlags]
  );

  const areEventsV2Enabled = useMemo(
    () =>
      featureFlags?.[EFeatureFlag.DASHBOARD_EVENTS_V2],
    [featureFlags]
  );

  const filteredEvents = useMemo(() => {
    if (category === EEventCategories.WEB_STORE && !areDeeplinksEnabled) {
      return events.filter(
        (event) =>
          event.value !== EPublisherEventValue.APPROVAL_REQUEST_CLOSED &&
          event.value !== EPublisherEventValue.APPROVAL_REQUEST_DISPLAYED
      );
    }
    return events;
  }, [areDeeplinksEnabled, category, events]);

  const getToggleHandler = (
    isEnabled: boolean,
    eventKey: string,
    version: 'v1' | 'v2'
  ) => {
    return () => {
      const isNewVersion = version === 'v2';
      const sourceEvents = isNewVersion ? enabledEventsV2 : enabledEvents;

      const updatedEvents = isEnabled
        ? sourceEvents.filter((e: string) => e !== eventKey)
        : [...sourceEvents, eventKey];
      const payloadKey = isNewVersion
        ? 'enabledWebhookEventsV2'
        : 'enabledWebhookEvents';

      updateIntegration.mutate(
        { [payloadKey]: updatedEvents },
        {
          onSuccess: () => {
            showToast({
              message: 'Webhook event updated successfully',
              status: EToasterStatus.SUCCESS
            });
          },
          onError: () => {
            showToast({
              message: 'Something went wrong with updating the event',
              status: EToasterStatus.ERROR
            });
          }
        }
      );
    };
  };

  const columns = [
    {
      field: '_id',
      headerName: '',
      disableReorder: true,
      flex: 1
    },
    {
      field: 'name',
      headerName: 'Trigger',
      disableReorder: true,
      flex: 1.5,
      sortable: false,
      renderCell: (params: any) => {
        const isDeprecated = params.row.version === 'v1' && areEventsV2Enabled;
        return `${params.row.name}${isDeprecated ? ' (deprecated)' : ''}`;
      }
    },
    {
      field: 'key',
      headerName: 'Key',
      disableReorder: true,
      flex: 1
    },
    {
      field: 'value',
      headerName: 'Event Name',
      disableReorder: true,
      flex: 1.5,
      sortable: false
    },
    {
      field: 'subCategory',
      headerName: 'Category',
      disableReorder: true,
      flex: 1,
      sortable: false
    },
    {
      field: 'isActive',
      headerName: 'Is Active',
      disableReorder: true,
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        const isNewVersion = params.row.version === 'v2';
        const sourceEvents = isNewVersion ? enabledEventsV2 : enabledEvents;
        const isEnabled = sourceEvents.includes(params.row.key);
        const handleToggle = getToggleHandler(
          isEnabled,
          params.row.key,
          params.row.version
        );
        return (
          <CustomizedSwitch
            status={isEnabled}
            texts={[]}
            functions={[handleToggle, handleToggle]}
          />
        );
      }
    }
  ];

  const rows = filteredEvents.map((event: PublisherEventType, i: number) => ({
    _id: i,
    name: event.displayName,
    value: event.value,
    key: event.value,
    subCategory: event?.subCategory,
    version: event.version
  }));

  return (
    <Grid container columnSpacing={4}>
      <Grid item mt={5} xs={15}>
        <AcCard stackContainer={false} title={category}>
          <DataTable
            columns={columns}
            rows={rows}
            defaultHiddenFields={['_id', 'key']}
            loading={false}
            onNoData={
              isTableDataEmpty ? (
                <FirstActionModal
                  headline="No events found"
                  text="Please check your spelling or try different keywords."
                />
              ) : (
                <FirstActionModal
                  headline="No exact matches"
                  text="Try changing or removing your filter or adjusting your search area"
                />
              )
            }
            className="eventsTable"
          />
        </AcCard>
      </Grid>
    </Grid>
  );
};

export default EventCategorySection;
